// main.js
import { loadViewerData } from "./data.js";
import { TileManager } from "./tiles.js";
import { OverlayManager } from "./overlays.js";
import { UIMain } from "./ui-main.js";
import { createOverlayHighlighter } from "./overlayHighlight.js";
import { createOverlayEditor } from "./editor.js";
import { InfoLayerToolbar } from "./infoLayerToolbar.js";
import { OverlayToolbar } from "./overlayToolbar_target_ovs_v4.js";

import { mountCubeViewer } from "./cubeViewer.js";
import { mountModelViewer } from "./modelViewerAdapter.js";

import { showError, getQueryParams, numParam, strParam, makeEase, animateValue, animateViewTo } from "./utils.js";

(async () => {
  const hud  = document.getElementById("hud");
  const tip  = document.getElementById("tip");
  const err  = document.getElementById("err");
  const info  = document.getElementById("info");
 
  const QP = getQueryParams();
  const mode = strParam(QP,"mode", "raw");
  const dataPath = strParam(QP, "data", "./data.json");
  // ====== Load data.json ======
  let D;
  try {
    D = await loadViewerData(dataPath, mode=="raw");
  } catch (e) {
    showError(err, "Could not load ./data.json\n\n" + (e?.stack || e?.message || String(e)));
    hud.textContent = "ERROR: data.json not loaded";
    return;
  }

  const { RAW, page, BASE_W, BASE_H, TILE_SIZE, URL_TEMPLATE, MAX_Z, TITLE, view: viewCfg, overlays } = D;
  document.title = TITLE;

  // ====== Params ======
  const USE_HULL = numParam(QP, "hull", 0) === 1;

  const ANIM = {
    zoomMs: numParam(QP, "zoom", 1000),
    fadeMs: numParam(QP, "fade", 1000),
    speed: Math.max(0.05, numParam(QP, "speed", 1.0)),
    ease: strParam(QP, "ease", "cubic").toLowerCase(),
  };
  const easeFn = makeEase(ANIM.ease);
  const dur = (ms) => Math.max(1, Math.round(ms / ANIM.speed));
  const VIEW_ANIM_MS = dur(ANIM.zoomMs);
  const FADE_MS = dur(ANIM.fadeMs);

  // Start view overrides
  const urlStartZoom = numParam(QP, "z", NaN);
  const urlOffX = numParam(QP, "ox", NaN);
  const urlOffY = numParam(QP, "oy", NaN);

  const initZoom = Number.isFinite(urlStartZoom) ? urlStartZoom : viewCfg.startZoom;
  const initOffX = Number.isFinite(urlOffX) ? urlOffX : viewCfg.startOffX;
  const initOffY = Number.isFinite(urlOffY) ? urlOffY : viewCfg.startOffY;

  

  const uiMain = new UIMain(this, D);
  //uiMain._createHTML();
  uiMain.init(mode);
  const host = document.getElementById("container");

  // ====== Pluggable viewer surface ======
  // You can switch the viewer either via:
  //   - URL param: ?viewer=pixi2d|cube|model-viewer|custom
  //   - JSON: RAW.page.viewer = { type: "...", ... }
  const viewerCfg = RAW?.page?.viewer || RAW?.viewer || {};
  const viewerType = strParam(QP, "viewer", viewerCfg.type || "pixi2d").toLowerCase();

  // Non-PIXI viewers mount into #container and exit early.
  if (viewerType === "model-viewer") {
    try {
      await mountModelViewer({ host, data: D });
    } catch (e) {
      showError(err, "Could not mount model-viewer\n\n" + (e?.stack || e?.message || String(e)));
    }
    return;
  }

  if (viewerType === "cube") {
    try {
      mountCubeViewer({ host, data: D });
    } catch (e) {
      showError(err, "Could not mount cube viewer\n\n" + (e?.stack || e?.message || String(e)));
    }
    return;
  }

  if (viewerType === "custom") {
    // Load a custom viewer module that exports: mount({ host, data, params }) => { destroy? }
    try {
      const modUrl = viewerCfg.module;
      if (!modUrl) throw new Error("RAW.page.viewer.module missing");
      const mod = await import(modUrl);
      if (typeof mod.mount !== "function") throw new Error("Custom viewer module must export mount()...");
      await mod.mount({ host, data: D, params: QP });
    } catch (e) {
      showError(err, "Could not mount custom viewer\n\n" + (e?.stack || e?.message || String(e)));
    }
    return;
  }
  
  // ====== PIXI init ====== must be set by texture
  //PIXI.settings.SCALE_MODE = PIXI.SCALE_MODES.LINEAR;
  //PIXI.settings.MIPMAP_TEXTURES = PIXI.MIPMAP_MODES.ON;

  const app = new PIXI.Application({
    resizeTo: host,
    backgroundAlpha: 0,
    antialias: true,
    autoDensity: true,
    resolution: Math.max(1, window.devicePixelRatio || 1),
  });
  host.appendChild(app.view);
  
  const world = new PIXI.Container();
  world.sortableChildren = true;
  app.stage.addChild(world);

  // Overlay layers above tiles
  const maskLayer = new PIXI.Container(); maskLayer.zIndex = 15000;
  const polyLayer = new PIXI.Container(); polyLayer.zIndex = 20000;
  world.addChild(maskLayer, polyLayer);

  // ====== view state ======
  // Zoom limits: can be overridden via data.json -> view.zoom.{minimum,maximum}
  let MIN_SCALE = Number.isFinite(viewCfg?.minZoom) ? Number(viewCfg.minZoom) : 0.10;
  let MAX_SCALE = Number.isFinite(viewCfg?.maxZoom) ? Number(viewCfg.maxZoom) : Math.pow(2, Math.max(0, MAX_Z));
  if (MIN_SCALE <= 0) MIN_SCALE = 0.10;
  if (MAX_SCALE <= 0) MAX_SCALE = Math.pow(2, Math.max(0, MAX_Z));
  if (MAX_SCALE < MIN_SCALE) {
    const t = MAX_SCALE; MAX_SCALE = MIN_SCALE; MIN_SCALE = t;
  }
  // Avoid floating-point jitter close to min/max (can cause LOD flicker at limits)
  const SCALE_EPS = 1e-6;
  var BASE_SCALE = 0.8;
  const FIT_PADDING_PX = 40;
  const ZOOM_STEP = 1.18;

  function clampScaleSnap(s) {
    // Snap to exact limits when very close to them.
    if (s >= MAX_SCALE * (1 - SCALE_EPS)) return MAX_SCALE;
    if (s <= MIN_SCALE * (1 + SCALE_EPS)) return MIN_SCALE;
    return Math.max(MIN_SCALE, Math.min(MAX_SCALE, s));
  }

  const view = { tx: 0, ty: 0, scale: 1 };
  function applyTransform() {
    world.scale.set(view.scale);
    world.position.set(view.tx, view.ty);
  }

  // ====== managers ======
  const tiles = new TileManager({ app, world, BASE_W, BASE_H, TILE_SIZE, URL_TEMPLATE, MAX_Z });
  const overlaysM = new OverlayManager({ overlays, page, BASE_W, BASE_H, maskLayer, polyLayer });

  // ====== base drawings ======
  overlaysM.drawBase();

  const isEditMode = (mode === "edit" || mode === "expert");

  let editor = null;
  if (isEditMode) {
    editor = createOverlayEditor({
      app,
      world,
      overlaysM,
      view,
      applyTransform,
      scheduleTilesRedraw,
      BASE_W,
      BASE_H,
    });
 // optional: expose for debugging
    window.overlayEditor = editor;
 
    // parent element: overlay it on top of the viewer container
    const host = document.getElementById("container");
    host.style.position = "relative";

    const toolbarHost = document.createElement("div");
    toolbarHost.style.position = "absolute";
    toolbarHost.style.left = "0";
    toolbarHost.style.top = "0";
    toolbarHost.style.right = "0";
    toolbarHost.style.pointerEvents = "none"; // allow clicks only on toolbar itself
    host.appendChild(toolbarHost);

    const toolbar = new InfoLayerToolbar({
      page,
      overlaysM,
      onInfoLayerChanged: (idx) => {
        if (window.overlayEditor?.clearSelection) window.overlayEditor.clearSelection();

        // optional hook: clear current highlight etc.
        // overlaysM.drawBase() is already called by setActiveInfoLayerIndex
      },
    });

    // make toolbar clickable while keeping overlay container transparent
    const clickLayer = document.createElement("div");
    clickLayer.style.position = "absolute";
    clickLayer.style.left = "0";
    clickLayer.style.top = "0";
    clickLayer.style.pointerEvents = "auto";
    toolbarHost.appendChild(clickLayer);

    toolbar.mount(clickLayer);

    window.infoLayerToolbar = toolbar; // debug


  }
  if (isEditMode) {
    const host = document.getElementById("container");
    host.style.position = "relative";
  
    const toolbarHost = document.createElement("div");
    toolbarHost.style.position = "absolute";
    toolbarHost.style.left = "0";
    toolbarHost.style.top = "0";
    toolbarHost.style.right = "0";
    toolbarHost.style.pointerEvents = "none";
    host.appendChild(toolbarHost);
  
    const clickLayer = document.createElement("div");
    clickLayer.style.position = "absolute";
    clickLayer.style.left = "0";
    clickLayer.style.top = "0";
    clickLayer.style.right = "0";
    clickLayer.style.pointerEvents = "auto";
    toolbarHost.appendChild(clickLayer);
  
    const ovToolbar = new OverlayToolbar({ overlaysM, editor, data: RAW });
    ovToolbar.mount(clickLayer);
  
    window.overlayToolbar = ovToolbar;
  }
  


  // ====== HUD update ======
  function updateHud(extra = {}) {
    return;
    const zInfo = extra.zInfo || tiles.pickZ(view.scale);
    const maskInfo = `alpha=${overlaysM.currentMask.alpha.toFixed(2)} (A=${overlaysM.maskA.alpha.toFixed(2)} B=${overlaysM.maskB.alpha.toFixed(2)})`;
    const outInfo = `cur=${overlaysM.currentActiveOutline.alpha.toFixed(2)} next=${overlaysM.nextActiveOutline.alpha.toFixed(2)}`;

    const loaded = extra.loaded ?? Array.from(tiles.sprites?.values?.() || []).filter(s => s.loaded).length;
    const size = extra.spritesSize ?? (tiles.sprites?.size ?? 0);

    hud.textContent =
`title:  ${TITLE}
base:   ${BASE_W.toFixed(1)}×${BASE_H.toFixed(1)}
z:      ${zInfo} / ${MAX_Z}   scale=${view.scale.toFixed(3)}
tiles:  ${size} (loaded ${loaded})
active: ${active ? (active.overlayId + " (poly#" + active.polyIndex + ")") : "-"}
mask:   ${maskInfo}
outline alpha: ${outInfo}
anim:   zoom=${VIEW_ANIM_MS}ms fade=${FADE_MS}ms speed=${ANIM.speed} ease=${ANIM.ease}`;
  }

  // ====== redraw pipeline ======
  function requestTilesAndRedraw() {
    const info = tiles.requestTiles(view);
    updateHud({ zInfo: info.z, spritesSize: info.spritesSize, loaded: info.loaded });
  }
  function scheduleTilesRedraw() {
    tiles.scheduleRedraw(requestTilesAndRedraw);
  }

  function fitWorldToHeightAndCenter() {
    // scale so BASE_H maps to screen height
   
    const vw = app.screen.width, vh = app.screen.height;
    const s = Math.max(
        MIN_SCALE,
        Math.min(MAX_SCALE, Math.min(vw / BASE_W, vh / BASE_H))
      ) * BASE_SCALE;
   
    //const s = app.screen.height / BASE_H;
  
    view.scale = s;
  
    // center the scaled base rect inside the screen
    view.tx = (app.screen.width  - BASE_W * s) / 2;
    view.ty = (app.screen.height - BASE_H * s) / 2;
  
    applyTransform();
    scheduleTilesRedraw(); // request correct tiles for the new view
  }
  
  // ====== crossfade (mask + active outline) ======
  let isAnimating = false;
  let active = null;

  async function crossfadeToSelection(selection, wantMaskAlpha) {
    overlaysM.buildInverseMaskInto(overlaysM.nextMask, selection);
    overlaysM.nextMask.alpha = 0;

    overlaysM.buildActiveOutlineInto(overlaysM.nextActiveOutline, selection);
    overlaysM.nextActiveOutline.alpha = 0;

    const fromMaskA = overlaysM.currentMask.alpha;
    const toMaskA = 0;
    const toMaskB = wantMaskAlpha;

    const fromOutA = overlaysM.currentActiveOutline.alpha;
    const toOutA = 0;
    const toOutB = selection ? 1 : 0;

    await animateValue(0, 1, FADE_MS, easeFn, (v, t, e) => {
      overlaysM.currentMask.alpha = fromMaskA + (toMaskA - fromMaskA) * e;
      overlaysM.nextMask.alpha = 0 + (toMaskB - 0) * e;

      overlaysM.currentActiveOutline.alpha = fromOutA + (toOutA - fromOutA) * e;
      overlaysM.nextActiveOutline.alpha = 0 + (toOutB - 0) * e;

      scheduleTilesRedraw();
    });

    overlaysM.swapAfterFade();
  }

  // ====== interaction ======
  app.stage.eventMode = "static";
  app.stage.hitArea = app.screen;

  // pinch + pan state
  const pointers = new Map(); // pointerId -> {x,y}
  let pinchStartDist = 0;
  let pinchStartScale = 1;
  let pinchStartTx = 0;
  let pinchStartTy = 0;
  let pinchStartCenter = null;

  const pDist = (a,b)=>Math.hypot(a.x-b.x, a.y-b.y);
  const pCent = (a,b)=>({ x:(a.x+b.x)/2, y:(a.y+b.y)/2 });

  function beginPinchIfTwo() {
    if (pointers.size !== 2) return;
    const pts = Array.from(pointers.values());
    pinchStartDist = pDist(pts[0], pts[1]);
    pinchStartScale = view.scale;
    pinchStartTx = view.tx;
    pinchStartTy = view.ty;
    pinchStartCenter = pCent(pts[0], pts[1]);
  }

  function applyPinchFromCurrent() {
    if (pointers.size !== 2 || !pinchStartCenter || pinchStartDist <= 0) return;
    const pts = Array.from(pointers.values());
    const d = pDist(pts[0], pts[1]);
    const c = pCent(pts[0], pts[1]);

    const factor = d / Math.max(1e-6, pinchStartDist);
    // Clamp + snap so we don't jitter between MAX and just-below-MAX when user keeps pinching.
    const nextScale = clampScaleSnap(pinchStartScale * factor);

    const bx = (pinchStartCenter.x - pinchStartTx) / pinchStartScale;
    const by = (pinchStartCenter.y - pinchStartTy) / pinchStartScale;

    view.scale = nextScale;
    view.tx = c.x - bx * nextScale;
    view.ty = c.y - by * nextScale;

    applyTransform();
    scheduleTilesRedraw();
  }

  let suppressTapUntil = 0;
  let ignoreMovesUntilNoPointers = false;
  let dragging = false;
  let last = null;
  let moveCount = 0;

  app.renderer.on("resize", () => {
    fitWorldToHeightAndCenter();
  });

  app.stage.on("pointerdown", (e) => {
    if (isEditMode) return;   // ✅ disable viewer drag/pinch in edit mode
    if (isAnimating) return;
    dragging = true;
    last = e.global.clone();
    moveCount = 0;
    pointers.set(e.pointerId, { x: e.global.x, y: e.global.y });
    if (pointers.size === 2) { dragging = false; last = null; beginPinchIfTwo(); }
  });
  const onPointerUp = (e) => {
    if (isEditMode) return;   // ✅ disable viewer drag/pinch in edit mode
    pointers.delete(e.pointerId);
    if (pointers.size < 2) pinchStartCenter = null;
    if (pointers.size === 0) ignoreMovesUntilNoPointers = false;
    dragging = false; last = null;
  };
  app.stage.on("pointerup", onPointerUp);
  app.stage.on("pointerupoutside", onPointerUp);

  app.stage.on("pointermove", (e) => {
    if (isEditMode) return;   // ✅ disable viewer drag/pinch in edit mode
    if (isAnimating) return;
    if (ignoreMovesUntilNoPointers) return;

    if (pointers.has(e.pointerId)) pointers.set(e.pointerId, { x: e.global.x, y: e.global.y });
    if (pointers.size === 2) { applyPinchFromCurrent(); suppressTapUntil = performance.now() + 350; return; }
    if (!dragging || !last) return;

    const g = e.global;
    view.tx += g.x - last.x;
    view.ty += g.y - last.y;
    last = g.clone();
    moveCount++;
    applyTransform();
    if (e.pointerType === "touch") suppressTapUntil = performance.now() + 250;
    scheduleTilesRedraw();
  });

  app.stage.on("pointertap", async (e) => {
    if (isEditMode) return; // editor handles selection/creation

    if (e.pointerType === "touch" && performance.now() < suppressTapUntil) return;
    if (moveCount > 3) return;
  
    const inv = 1 / view.scale;
    const bx = (e.global.x - view.tx) * inv;
    const by = (e.global.y - view.ty) * inv;
  
    // one call handles: pick -> (clear/reset) OR (highlight+zoom+fade)
    await highlighter.handleTapAtBasePoint(bx, by, { paddingPx: FIT_PADDING_PX });
  
    if (e.pointerType === "touch") suppressTapUntil = performance.now() + 250;
  });
  

  function zoomAtScreenPoint(factor, sx, sy) {
    if (isAnimating) return;
    const old = view.scale;
    // If we are essentially at the limits, ignore further zoom attempts beyond them.
    if (old >= MAX_SCALE * (1 - SCALE_EPS) && factor > 1) return;
    if (old <= MIN_SCALE * (1 + SCALE_EPS) && factor < 1) return;

    const next = clampScaleSnap(old * factor);
    // float-safe equality
    if (Math.abs(next - old) <= Math.max(1e-12, old * SCALE_EPS)) return;

    const bx = (sx - view.tx) / old;
    const by = (sy - view.ty) / old;

    view.scale = next;
    view.tx = sx - bx * next;
    view.ty = sy - by * next;

    applyTransform();
    scheduleTilesRedraw();
  }

  
  app.view.addEventListener("wheel", (e) => {
    e.preventDefault();
  
    const rect = app.view.getBoundingClientRect();
    const sx = e.clientX - rect.left;
    const sy = e.clientY - rect.top;
  
    const factor = (e.deltaY < 0) ? ZOOM_STEP : (1 / ZOOM_STEP);
    zoomAtScreenPoint(factor, sx, sy);
  }, { passive: false });
  
  function baseFitScale(app) {
    const vw = app.screen.width, vh = app.screen.height;
    return Math.max(MIN_SCALE, Math.min(MAX_SCALE, Math.min(vw / BASE_W, vh / BASE_H)));
  }

  function getFitToScreenTarget() {
    const vw = app.screen.width, vh = app.screen.height;
    const scale = Math.max(MIN_SCALE, Math.min(MAX_SCALE, Math.min(vw / BASE_W, vh / BASE_H)));
    return {
      scale,
      tx: (vw - BASE_W * scale) / 2,
      ty: (vh - BASE_H * scale) / 2,
    };
  }
  // buttons
  function fitToScreen() {
    const vw = app.screen.width, vh = app.screen.height;
    const scale = Math.max(MIN_SCALE, Math.min(MAX_SCALE, Math.min(vw / BASE_W, vh / BASE_H)));
    view.scale = scale;
    view.tx = (vw - BASE_W * scale) / 2;
    view.ty = (vh - BASE_H * scale) / 2;
    applyTransform();
    scheduleTilesRedraw();
  }

  
  
  function viewFitOverlayToScreen(app, bounds, zoomMultiplier = 1) {
    const vw = app.screen.width;
    const vh = app.screen.height;
  
    const pad = FIT_PADDING_PX; // you already have this constant :contentReference[oaicite:1]{index=1}
    const availW = Math.max(1, vw - 2 * pad);
    const availH = Math.max(1, vh - 2 * pad);
  
    const w = Math.max(1e-6, bounds.maxx - bounds.minx);
    const h = Math.max(1e-6, bounds.maxy - bounds.miny);
  
    // scale as large as possible while keeping entire overlay visible
    let scale = Math.min(availW / w, availH / h);
  
    // optional multiplier (e.g. ov.zoom = 1.0 means exactly fit, 1.2 zooms in 20% but may crop)
    scale *= (zoomMultiplier ?? 1);
  
    scale = Math.max(MIN_SCALE, Math.min(MAX_SCALE, scale));
  
    // center overlay
    const cx = (bounds.minx + bounds.maxx) / 2;
    const cy = (bounds.miny + bounds.maxy) / 2;
  
    return {
      scale,
      tx: (vw / 2) - cx * scale,
      ty: (vh / 2) - cy * scale,
    };
  }
  
  const highlighter = createOverlayHighlighter({
    app,
    overlaysM,
    page,
    view,
    BASE_W,
    BASE_H,
    MIN_SCALE,
    MAX_SCALE,
    BASE_SCALE,
    VIEW_ANIM_MS,
    easeFn,
    applyTransform,
    scheduleTilesRedraw,
    animateViewTo,
    crossfadeToSelection,
    fitToScreen, // your “initial state”
  });
  
  // (optional) expose globally so you can call from outside main/IIFE
  window.overlayHighlighter = highlighter;

  async function clearSelection() {
    if (isAnimating) return;
    isAnimating = true;
    ignoreMovesUntilNoPointers = true;
    dragging = false; last = null;
    pointers.clear();

    active = null;
    await crossfadeToSelection(null, 0);

/*    tip.innerHTML = `<span class="badge">Maus</span> Ziehen = Pan · Wheel = Zoom · Klick = aktiviere kleinstes Polygon (Overlay-Gruppe) (Zoom+Center + Crossfade)
      <span style="display:inline-block; width:10px;"></span>
      <span class="btn" id="btnFit">Fit</span>
      <span class="btn" id="btnClear">Clear</span>`;*/
    wireButtons();

    isAnimating = false;
    scheduleTilesRedraw();
  }

  function wireButtons() {
    const b1 = document.getElementById("btnFit");
    const b2 = document.getElementById("btnClear");
    if (b1) b1.onclick = fitToScreen;
    if (b2) b2.onclick = clearSelection;
  }

  // init
  function initFromData() {
    const vw = app.screen.width, vh = app.screen.height;

  view.scale = clampScaleSnap(initZoom);
    view.tx = (vw - BASE_W * view.scale) / 2;
    view.ty = (vh - BASE_H * view.scale) / 2;

    view.tx -= initOffX * view.scale;
    view.ty -= initOffY * view.scale;

    fitToScreen();
    applyTransform();
    wireButtons();
    scheduleTilesRedraw();
  }
  

  window.addEventListener("resize", () => scheduleTilesRedraw());

  initFromData();
  requestAnimationFrame(() => {
    try { app.renderer.resize(host.clientWidth, host.clientHeight); } catch {}
    scheduleTilesRedraw();
  });
})();
